import { Requisition, Sample } from '../../types/types'
import { PencilSimpleIcon } from 'prove-common'
import * as Mtds from '@mattilsynet/design/react'
import { FinalizeSampleButton } from '../finalize-sample/FinalizeSampleButton'
import {
  getSampleResultBlockers,
  isSampleReadyToBeFinalized
} from '../../utils/sampleUtils'
import { isFollowUpNull } from '../../utils/requisitionUtils'
import { useState } from 'react'
import { SampleCancellationModal } from '../sample-cancellation/SampleCancellationModal'

type SampleResultSectionProps = {
  sample: Sample
  requisition: Requisition
  handleEditFollowUp: () => void
}

export const SampleResultSection = ({
  sample,
  requisition,
  handleEditFollowUp
}: SampleResultSectionProps) => {
  const followUpNull = isFollowUpNull(requisition)
  const [resolveOpen, setResolveOpen] = useState(false)
  const hasResultDetails =
    requisition?.followUp != null || requisition?.followUpComment != null
  const isReadyToBeFinalized = isSampleReadyToBeFinalized(sample, requisition)
  const { activeSubSampleCount, missingResults } = getSampleResultBlockers(
    sample,
    requisition
  )
  const canResolveMissingResults =
    sample.state === 'ReadyToProcess' && missingResults.length > 0
  const showBlockers =
    sample.state === 'ReadyToProcess' && !isReadyToBeFinalized
  const blockerMessage =
    activeSubSampleCount === 0
      ? 'Prøven kan ikke ferdigstilles fordi den ikke har aktive delprøver.'
      : 'Prøven kan ferdigstilles når resultatene for alle aktive delprøver er vurdert.'
  const followUpColor = requisition.followUp ? 'warning' : 'neutral'
  const followUpDescription = requisition.followUp
    ? 'Prøven skal følges opp.'
    : 'Prøven trenger ingen oppfølging.'

  if (!hasResultDetails && !isReadyToBeFinalized && !showBlockers) return null

  return (
    <Mtds.Grid data-gap='4'>
      {showBlockers && !canResolveMissingResults && (
        <Mtds.Alert data-color='warning'>
          <Mtds.Grid data-gap='2'>
            <p>{blockerMessage}</p>
          </Mtds.Grid>
        </Mtds.Alert>
      )}
      {canResolveMissingResults && (
        <Mtds.Alert data-color='warning'>
          <Mtds.Grid data-gap='3'>
            <Mtds.Heading as='h3' data-size='xs'>
              Avklar manglende resultater
            </Mtds.Heading>
            <p>
              Noen delprøver mangler analyseresultater. Vent på resultatene,
              eller kanseller delprøvene hvis de ikke skal analyseres.
            </p>
            <Mtds.Flex>
              <Mtds.Button
                type='button'
                data-variant='secondary'
                onClick={() => setResolveOpen(true)}
              >
                Avklar manglende resultater
              </Mtds.Button>
            </Mtds.Flex>
          </Mtds.Grid>
        </Mtds.Alert>
      )}
      {resolveOpen && (
        <SampleCancellationModal
          missingResultsOnly
          requisition={requisition}
          sampleId={sample.id}
          onClose={() => setResolveOpen(false)}
        />
      )}
      {hasResultDetails && (
        <Mtds.Alert data-color={followUpColor}>
          <Mtds.Flex data-align='start' data-justify='space-between'>
            <Mtds.Grid data-gap='3'>
              <Mtds.Heading as='h3' data-size='xs'>
                Oppfølging
              </Mtds.Heading>
              <p>{followUpDescription}</p>
            </Mtds.Grid>
            {requisition?.followUp != null && (
              <Mtds.Button
                data-variant='tertiary'
                data-size='sm'
                data-tooltip='Rediger oppfølging'
                onClick={handleEditFollowUp}
              >
                <PencilSimpleIcon />
                Rediger
              </Mtds.Button>
            )}
          </Mtds.Flex>
          {requisition?.followUpComment != null ? (
            <Mtds.Grid data-gap='1'>
              <strong>Kommentar</strong>
              <p>{requisition.followUpComment}</p>
            </Mtds.Grid>
          ) : (
            <Mtds.Muted>Det er ikke lagt inn en kommentar.</Mtds.Muted>
          )}
        </Mtds.Alert>
      )}
      {isReadyToBeFinalized && (
        <Mtds.Card
          as={Mtds.Grid}
          className='bg-surface-tinted'
          data-color={followUpNull ? 'success' : 'info'}
        >
          {followUpNull ? (
            <p>Prøven er klar for å ferdigstilles og avskrives i arkivet.</p>
          ) : (
            <p>
              Laboratoriet har oppdatert resultatene. Prøven må vurderes og
              ferdigstilles på nytt.
            </p>
          )}
          <FinalizeSampleButton requisition={requisition} />
        </Mtds.Card>
      )}
    </Mtds.Grid>
  )
}
